<script context="module">
  import {popupUtil} from '@/helper/popup';
  import {envUtil} from '@/helper/env';
  import {dialogInfo$, dialogPageMount$, dialogStatus$, query$} from '@/modules/popups/sensors-data-popups/stream';
  import SensorsTextDialog from '@/modules/popups/sensors-data-popups/SensorsTextDialog.svelte';
  import SensorsImageDialog from '@/modules/popups/sensors-data-popups/SensorsImageDialog.svelte';
  import IpRestricted from './ip-restricted/IpRestricted.svelte';
  import NewbieRewardsAlert from './newbie-rewards-alert/NewbieRewardsAlert.svelte';

  let subscription = null;
  let currentPath = null;

  function intSdWebPopup(path, lang) {
    currentPath = path;
    if (!subscription) {
      dialogPageMount$.next(true);
      query$.next({channel: 'WEB', scene: 'UNKNOWN', platform: 'WEB', language: lang || 'en', site: envUtil.isJapanSite ? 'JP' : 'GLOBAL'});
      subscription = dialogInfo$.subscribe(([contents, validatePath, updateStorage]) => {
        if (contents && contents.length > 0) {
          const content = validatePath(contents, currentPath);
          if (!content) {
            return;
          }
          updateStorage && updateStorage(content);
          if (content.templateType === 'image') {
            popupUtil.create(SensorsImageDialog, {
              content,
            });
            dialogStatus$.next(true);
            return;
          }
          if (content.templateType === 'text') {
            popupUtil.create(SensorsTextDialog, {
              content,
            });
            dialogStatus$.next(true);
          }
        }
      });
    }
  }

  function closePage() {
    dialogPageMount$.next(false);
  }
</script>

<script>
  import {filter, take} from 'rxjs/operators';
  import {getContext, onDestroy, onMount} from 'svelte';
  import {goto, stores} from '@sapper/app';
  import {OFFLINE_PATHS, PATHS} from '@/const/paths';
  import {IMAGES_NEWS} from '@/const/images';
  import {sd} from '@/da';
  import {csRobotUtil} from '@/da/cs-robot';
  import {mc} from '@/core';
  import {langUtil} from '@/helper/lang';
  import {env} from '@/helper/env';
  import {numUtil} from '@/helper/num';
  import {getLocationSearch} from '@/helper/page';
  import {userApi} from '@/api/user';
  import {referral$} from '@/store/referral';
  import {userInfo$, isLogin$} from '@/store/user';
  import {fixLink$, lang$} from '@/store/lang';
  import {userStatus$, activationStatus$} from '@/streams/auth';
  import {getCsRobotInfo, loadChatReady$} from '@/streams/cs-robot';
  import {loadUserTag$, userTags$, userMemberLevel$, loadMemberLevel$, getBaseKycCheck} from '@/streams/user';
  import {innerWidth$, innerHeight$, aspectRatio$} from '@/streams/window-inner-size';
  import {ipRestrictedConfig$} from '@/modules/components/ip-restricted/streams';
  import {basicKycDialog$} from '@/modules/popups/basic-kyc/streams';
  import {getAllKycSummary} from '@/modules/account/overview/stream-kyc';
  import CompatibilityPage from '@/modules/compatibility/CompatibilityPage.svelte';
  import BuyPremiumPopup from '@/modules/popups/BuyPremiumPopup.svelte';
  import BuySuccessPopup from '@/modules/popups/BuySuccessPopup.svelte';
  import ExportHistoryPopup from '@/modules/popups/export-history/ExportHistoryDialog.svelte';
  import ThreeMonthsTipsDialog from '@/modules/popups/ThreeMonthsTipsDialog.svelte';
  import ToastView from '@/modules/popups/ToastView.svelte';
  import WelcomeBackDialog from '@/modules/popups/welcome-back/WelcomeBackDialog.svelte';
  import BasicKycDialog from '@/modules/popups/basic-kyc/BasicKycDialog.svelte';
  import SubAccountLogoutDialog from '@/modules/popups/basic-kyc/SubAccountLogoutDialog.svelte';
  export let customTwitter = false;
  export let customOpenGraph = false;
  export let hasLoadZeSnippet = false;
  export let defaultReferral = {};

  let innerWidth = 1200;
  let innerHeight = 768;

  const LG = getContext('LG');
  const {page} = stores();
  const canonicalWhiteList = [PATHS.CONTRACT_PERPETUAL_CONTRACT.pathname, PATHS.REGISTER_VT1.pathname, PATHS.MARKETS.pathname];
  const fullscreenWhiteList = [PATHS.LOGIN.pathname, PATHS.REGISTER.pathname, PATHS.WELCOME.pathname, PATHS.RESET_PASSWORD.pathname, PATHS.RESET_SECURITY.pathname];
  const hiddenChatWhiteList = [PATHS.ABOUT_AMBASSADOR_KEVIN_DE_BRUYNE.pathname, PATHS.KOL_LANDING.pathname];
  const hiddenCsRobotList = [];

  $: process.browser && updateHeight($page.path);
  $: process.browser && $isLogin$ && afterLogin();
  $: updateUserTag($loadChatReady$, $userTags$, $userInfo$, $userMemberLevel$);
  $: updateIpConfig($loadChatReady$, $ipRestrictedConfig$);
  $: updateInnerSize(innerWidth, innerHeight);

  onMount(() => {
    const offlines = OFFLINE_PATHS.map((link) => $fixLink$(link));
    if (offlines.includes($page.path)) {
      goto($fixLink$(PATHS.HOME.pathname));
    }
  });
  onMount(() => mc.send('captcha-status', 'refresh'));
  onMount(() => {
    userStatus$
      .pipe(
        filter((userStatus) => userStatus !== 'Init'),
        take(1)
      )
      .subscribe((auth_status) => {
        const {irclickid} = $page.query;
        const params = !!irclickid ? {auth_status, click_id: irclickid, click_id_source: 'impact'} : {auth_status};
        sd.pageView(params);
      });
  });
  onMount(() => {
    if (env.isApp) {
      return;
    }
    intSdWebPopup($page.path, $lang$);
  });
  onMount(() => {
    if (env.isApp || env.isBot || hasLoadZeSnippet) {
      return;
    }
    const {pathname} = langUtil.parsePathname($page.path);
    if (env.isMobile) {
      if (hiddenChatWhiteList.indexOf(pathname) === -1) {
        setTimeout(loadZeSnippet, 5000);
      }
      return;
    }
    if (!hiddenCsRobotList.includes(pathname)) {
      setTimeout(loadZeSnippet, 2000);
    }
    createWelcomeBackDialog($isLogin$, $activationStatus$);
  });
  onMount(() => {
    return isLogin$.subscribe(async (isLogin) => {
      if (!isLogin) {
        return;
      }
      const baseKycStatus = localStorage.getItem('base-kyc-status');
      if (!baseKycStatus) {
        return;
      }
      const {baseKycStrategy} = await getBaseKycCheck();
      if (baseKycStrategy === 1) {
        localStorage.removeItem('base-kyc-status');
        return;
      }
      if ($basicKycDialog$) {
        return;
      }
      if ($userInfo$.isSub && baseKycStrategy === 3) {
        const kycDialog = popupUtil.create(SubAccountLogoutDialog);
        basicKycDialog$.next(kycDialog);
        return;
      }
      const {kycFlag} = await getAllKycSummary();
      if (!$userInfo$.isSub && (baseKycStrategy === 2 || baseKycStrategy === 3) && kycFlag === 0) {
        const kycDialog = popupUtil.create(BasicKycDialog, {LG, closeable: baseKycStrategy === 2});
        basicKycDialog$.next(kycDialog);
      }
    });
  });
  onMount(() => {
    const {referralCode = defaultReferral && defaultReferral.referralCode, group = defaultReferral && defaultReferral.group} = getLocationSearch();
    referralCode && referral$.set({code: referralCode, group});
    group && numUtil.isNumber(group) && userApi.addGroupClickCnt(group);
  });
  onDestroy(() => closePage());

  function afterLogin() {
    loadUserTag$.next();
    loadMemberLevel$.next();
  }

  function updateHeight(path) {
    const pathname = langUtil.parsePathname(path).pathname;
    if (fullscreenWhiteList.indexOf(pathname) === -1) {
      document.body.style.height = 'auto';
      return;
    }
    document.body.style.height = '100%';
  }

  function checkCanonical() {
    if (env.isServer) {
      return false;
    }
    const pathname = langUtil.parsePathname($page.path).pathname;
    return canonicalWhiteList.indexOf(pathname) < 0;
  }

  async function loadZeSnippet() {
    const csRobotInfo = await getCsRobotInfo();
    csRobotUtil.init(csRobotInfo, $lang$, function () {
      loadChatReady$.next(true);
    });
  }

  function createWelcomeBackDialog(isLogin, activationStatus) {
    if (!isLogin || activationStatus === '0' || localStorage.getItem('ACTIVATION_STATUS') !== '1') {
      return;
    }
    popupUtil.create(WelcomeBackDialog, {LG});
  }

  function updateUserTag(loadChatReady, userTags, userInfo, userMemberLevel) {
    if (!loadChatReady) {
      return;
    }
    csRobotUtil.changeUserInfo(userInfo, userTags, userMemberLevel);
  }

  function updateIpConfig(loadChatReady, ipRestrictedConfig) {
    if (!loadChatReady) {
      return;
    }
    csRobotUtil.changeExpandInfo('IpConfig', ipRestrictedConfig);
  }

  function updateInnerSize(innerWidth, innerHeight) {
    innerWidth$.next(innerWidth);
    innerHeight$.next(innerHeight);
    if (env.isBrowser) {
      document.body.style.setProperty('--aspect-ratio', $aspectRatio$);
    }
  }
</script>

<style>
  :root {
    /* 菜单栏统一 */
    --menu-light: #303236;
    --menu-dark: #fff;
    --menu-light-hover: #0052FF;
    --menu-dark-hover: #3d7eff;
    --aspect-ratio: 1;

    --TM: #090A0A;
    --B1: #fafafa;
    --B2: #ffffff;
    --BM: #33D43E;
  }
</style>

<svelte:head>
  {#if checkCanonical()}
    <link rel="canonical" href={location.origin + (location.pathname !== '/' ? location.pathname : '')} />
  {/if}
  {#if !customTwitter}
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@Phemex_official" />
    <meta name="twitter:title" content="Phemex: Buy & Sell Crypto | Trade BTC and Crypto Derivatives" />
    <meta name="twitter:image" content={IMAGES_NEWS.OG_NORMAL} />
  {/if}
  {#if !customOpenGraph}
    <meta name="og:title" content="Phemex: Buy & Sell Crypto | Trade BTC and Crypto Derivatives" />
    <meta
      name="og:description"
      content="Phemex is the fastest crypto exchange and crypto derivatives platform. Buy, Sell & Earn Bitcoin, Ether, & altcoin. Trade perpetual futures with 100x Leverage."
    />
    <meta name="og:image" content={IMAGES_NEWS.OG_NORMAL} />
  {/if}
  {#if env.isJapanSite}
    <link rel="shortcut icon" href="https://img.phemex.com/v1/22c61c50-f468-405f-86cd-bbafb7b2d8e7/favicon-pulse-social.ico" />
  {:else}
    <link rel="shortcut icon" href="https://static.phemex.com/s/common/favicon-v2.ico" />
  {/if}
</svelte:head>

<svelte:window bind:innerWidth bind:innerHeight />

<IpRestricted />
<NewbieRewardsAlert />
<slot />
<CompatibilityPage />
<BuyPremiumPopup />
<BuySuccessPopup />
<ExportHistoryPopup />
<ThreeMonthsTipsDialog />
<ToastView />
